import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { request } from "utils/request";
import { useLoadingContext } from "app/contexts/LoadingContext";
import { useApp } from "app/contexts/AppContext";
import AppStoreGuidance from "./AppStoreGuidance";
import CustomToast from "app/components/common/CustomToast";
import {
  fetchSoloPricing,
  buildSoloPricingView,
} from "utils/helperFunctions/soloPricing";
import {
  hasActiveAppStoreSubscription,
  isAppStoreSubscriptionError,
  GENERIC_PAYMENT_ERROR_MESSAGE,
} from "utils/helperFunctions/subscriptionProviders";
import "./upgradePlanModal.css";
import { CheckLg } from "react-bootstrap-icons";

// Same-size grey stand-in for a price slot while the price request is in
// flight. The hidden text keeps the slot readable for screen readers.
const PricePlaceholder = ({ className = "" }) => (
  <span
    className={`price-placeholder ${className}`.trim()}
    data-testid="price-placeholder"
  >
    <span className="visually-hidden">Loading price</span>
  </span>
);

export default function UpgradePlanModal({ open, onClose }) {
  const [selectedPlan, setSelectedPlan] = useState("yearly");
  const [appStoreBlocked, setAppStoreBlocked] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  // null while the price request is in flight; the price slots then show
  // placeholders and the CTA drops the trial days.
  const [pricing, setPricing] = useState(null);
  // Reset on the same render that opens the dialog. Doing it in the effect
  // would paint the previous open's prices for one frame first.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setPricing(null);
  }
  const [errorToastMessage, setErrorToastMessage] = useState("");
  const { showMaskedLoading, hideMaskedLoading } = useLoadingContext();
  const { device } = useApp();
  const { profile } = useSelector((state) => state.userProfile);

  // Gate on active_subscription_providers — never subscription.provider.
  // A live App Store subscription must not be double-billed from the web,
  // so the checkout surface is replaced by the App Store guidance. This is
  // the single choke point for every entry that opens this modal.
  // `appStoreBlocked` covers the defense-in-depth path: the backend
  // returned 409 payment.app_store_subscription_active on checkout.
  const showAppStoreGuidance =
    hasActiveAppStoreSubscription(profile) || appStoreBlocked;

  const handlePlanChange = (plan) => {
    setSelectedPlan(plan);
  };

  const handleStartFreeTrial = async () => {
    try {
      // Show masked loading while processing
      showMaskedLoading();

      const response = await request(
        `${process.env.REACT_APP_API_URL}/payments/checkout-session/`,
        {
          method: "POST",
          body: JSON.stringify({
            tier: "Solo",
            interval: selectedPlan,
            // coupon: "PROMO20",
            success_redirect_url: `${process.env.REACT_APP_URL}`,
            cancel_redirect_url: `${process.env.REACT_APP_URL}`,
          }),
        }
      );

      // Hide loading before redirect
      hideMaskedLoading();
      window.location.href = response.url; // redirect to the customer portal
      onClose();
    } catch (error) {
      // Hide loading on error
      hideMaskedLoading();
      if (isAppStoreSubscriptionError(error)) {
        // Defense-in-depth: the backend blocked checkout because a live
        // App Store subscription exists — swap in the guidance in place.
        setAppStoreBlocked(true);
      } else {
        setErrorToastMessage(
          error?.user_error_msg || GENERIC_PAYMENT_ERROR_MESSAGE
        );
        setShowErrorToast(true);
      }
    }
  };

  const handleClose = () => {
    // Hide any loading state when modal closes
    hideMaskedLoading();
    onClose();
  };

  // Cleanup loading state when modal is unmounted or closed
  useEffect(() => {
    if (!open) {
      hideMaskedLoading();
      // Drop the 409 defence state so a reopen re-evaluates from the
      // (refetched) profile instead of a stale block, and drop any
      // pending error toast so it does not replay on the next open.
      setAppStoreBlocked(false);
      setShowErrorToast(false);
    }
  }, [open, hideMaskedLoading]);

  // Read the Solo prices on every false-to-true change of `open`, never on
  // mount: the dialog stays mounted (closed) in many screens. Each open starts
  // from the loading state (reset above) and owns one request. Closing or
  // unmounting aborts it and marks it stale, so a late answer cannot
  // overwrite a newer open.
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    let isCurrent = true;
    fetchSoloPricing({ signal: controller.signal }).then((body) => {
      if (isCurrent) setPricing(buildSoloPricingView(body));
    });
    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [open]);

  const trialDays = pricing ? pricing[selectedPlan].trialDays : null;

  return (
    <Modal
      show={open}
      onHide={handleClose}
      centered
      className="upgrade-plan-modal-wrapper"
    >
      <Modal.Body className="p-0">
        <div className="upgrade-plan-modal">
          <div className="upgrade-plan-modal-header">
            <span>
              {showAppStoreGuidance
                ? "Manage Your Subscription"
                : "Upgrade to Solo Plan"}
            </span>
            <button
              className="upgrade-plan-modal-close"
              onClick={handleClose}
              aria-label="Close"
            >
              &times;
            </button>
          </div>
          <hr className="upgrade-plan-modal-divider" />
          {showAppStoreGuidance ? (
            <>
              <div className="upgrade-plan-modal-body">
                <AppStoreGuidance />
              </div>
              <hr className="upgrade-plan-modal-divider" />
              <div className="upgrade-plan-modal-footer ">
                <button
                  className="upgrade-plan-modal-cta guidance w-100"
                  onClick={handleClose}
                >
                  Got it
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="upgrade-plan-modal-body">
                <div className="upgrade-plan-modal-desc">
                  You're currently on the free plan.
                  <br />
                  Upgrade to enhance your inspection reports with AI.
                </div>
                <div
                  className="upgrade-plan-modal-pricing"
                  data-testid="upgrade-plan-pricing"
                  aria-busy={!pricing}
                >
                  <div
                    className={`upgrade-plan-modal-price${
                      selectedPlan === "monthly" ? " selected" : ""
                    }`}
                    onClick={() => handlePlanChange("monthly")}
                  >
                    <div className="price-main">
                      {pricing ? pricing.monthly.price : <PricePlaceholder />}{" "}
                      <span className="price-unit">/month</span>
                    </div>
                    <div className="price-desc">Billed monthly</div>
                  </div>
                  <div
                    className={`upgrade-plan-modal-price${
                      selectedPlan === "yearly" ? " selected" : ""
                    }`}
                    onClick={() => handlePlanChange("yearly")}
                  >
                    {pricing?.savePercent && (
                      <span className="save-badge">
                        Save ~{pricing.savePercent}%
                      </span>
                    )}
                    <div className="price-main">
                      {pricing ? pricing.yearly.perMonth : <PricePlaceholder />}{" "}
                      <span className="price-unit">/month</span>
                    </div>
                    <div className="price-desc">
                      {pricing ? (
                        `${pricing.yearly.total} billed annually`
                      ) : (
                        <PricePlaceholder className="price-placeholder-desc" />
                      )}
                    </div>
                  </div>
                </div>
                <div className="upgrade-plan-modal-desc">
                  Everything in Free, plus: <br />
                </div>
                <ul className="upgrade-plan-modal-features">
                  <li>
                    <CheckLg /> Unlimited AI report generation
                  </li>
                  <li>
                    <CheckLg /> Smart Template import
                  </li>
                  <li>
                    <CheckLg /> Personalized AI Style
                  </li>
                  <li>
                    <CheckLg /> 24/7 Online Client Booking
                  </li>
                  <li>
                    <CheckLg /> Export Schedule to External Calendars
                  </li>
                  <li>
                    <CheckLg /> Email and chat Support
                  </li>
                </ul>
              </div>
              <hr className="upgrade-plan-modal-divider" />
              <div className="upgrade-plan-modal-footer ">
                <button
                  className="upgrade-plan-modal-cta w-100"
                  onClick={handleStartFreeTrial}
                >
                  Start Free Trial
                  {trialDays !== null && ` (${trialDays} days)`}
                </button>
                <div className="upgrade-plan-modal-cancel">
                  Cancel anytime during trial period
                </div>
              </div>
            </>
          )}
        </div>
        <CustomToast
          show={showErrorToast}
          onClose={() => setShowErrorToast(false)}
          message={errorToastMessage}
          variant="error"
          isMobile={device.isMobile}
        />
      </Modal.Body>
    </Modal>
  );
}
